import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { BrowserModule } from '@angular/platform-browser';
import { ColorPickerModule } from 'ngx-color-picker';
import { CookieModule } from 'ngx-cookie';
import { CustomTranslateHttpLoader, OscReferenceAppCommonModule } from './osc-reference-app-common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TimeagoModule } from 'ngx-timeago';
import { NgxCaptureModule } from 'ngx-capture';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import { AppRoutingModule } from './app-routing.module';
import { MaterialModule } from './material.module';
import { AppComponent } from './app.component';
import { UtilsService } from './_services/_utils.service';
import { ReportComponentService } from './_services/report-component.service';
import { ReportTemplateService } from './_services/report-template.service';
import { StorageService } from './_services/storage.service';
import { SubjectModelService } from './_services/subject-model.service';
import { AddNewTemplateDialogComponent } from './common-dialog/add-new-template-dialog/add-new-template-dialog.component';
import { CommonConfirmDialogComponent } from './common-dialog/common-confirm-dialog/common-confirm-dialog.component';
import { CommonDeleteDialogComponent } from './common-dialog/common-delete-dialog/common-delete-dialog.component';
import { CommonErrorMessageDialogComponent } from './common-dialog/common-error-message-dialog/common-error-message-dialog.component';
import { ImportNewTemplateDialogComponent } from './common-dialog/import-new-template-dialog/import-new-template-dialog.component';
import {
  EditableTextsValidationDialogComponent
} from './common-dialog/inspection-report-edit-text-validation-dialog/inspection-report-edit-text-validation-dialog.component';
import { ListDialogComponent } from './common-dialog/list-dialog/list-dialog.component';
import {
  ReportTemplateEditPropertyDialogComponent
} from './common-dialog/report-template-edit-property-dialog/report-template-edit-property-dialog.component';
import {
  ReportTemplateEditReportNameFormatDialogComponent
} from './common-dialog/report-template-edit-report-name-format-dialog/report-template-edit-report-name-format-dialog.component';
import {
  ReportTemplateImageUploadDialogComponent
} from './common-dialog/report-template-image-upload-dialog/report-template-image-upload-dialog.component';
import {
  ReportTemplateMoveConfirmDialogComponent
} from './common-dialog/report-template-move-confirm-dialog/report-template-move-confirm-dialog.component';
import { CommonSpinnerComponent } from './common-spinner/common-spinner.component';
import { getEnvironment } from './helpers/environment.helper';
import { ReportCustomElementComponent } from './report-elements/report-custom-element/report-custom-element.component';
import { ReportDataPlateComponent } from './report-elements/report-dataplate/report-dataplate.component';
import { ReportDropDownListComponent } from './report-elements/report-dropdown-list/report-dropdown-list.component';
import { ReportImageComponent } from './report-elements/report-image/report-image.component';
import { ReportIsoComponent } from './report-elements/report-iso/report-iso.component';
import { ReportLineComponent } from './report-elements/report-line/report-line.component';
import { ReportMeasurementPointComponent } from './report-elements/report-measurement-points/report-measurement-points.component';
import { ReportPageComponent } from './report-elements/report-page/report-page.component';
import { ReportPictureComponent } from './report-elements/report-picture/report-picture.component';
import { ReportRectangleComponent } from './report-elements/report-rectangle/report-rectangle.component';
import { ReportSectionComponent } from './report-elements/report-section/report-section.component';
import { ReportSignatureComponent } from './report-elements/report-signature/report-signature.component';
import { ReportSummaryComponent } from './report-elements/report-summary/report-summary.component';
import { ReportSubTotalComponent } from './report-elements/report-subtotal/report-subtotal.component';
import { ReportTableComponent } from './report-elements/report-table/report-table.component';
import { ReportTextComponent } from './report-elements/report-text/report-text.component';
import { VisualEventsComponent } from './report-elements/visual-events/visual-events.component';
import { CreateCustomElementComponent } from './report-template/create-custom-element/create-custom-element.component';
import { PropertyEditorComponent } from './report-template/property-preview-editor/property-preview-editor.component';
import { PropertyPreviewComponent } from './report-template/property-preview/property-preview.component';
import { ReportPreviewComponent } from './report-template/report-preview/report-preview.component';
import { ReportNewPreviewComponent } from './report-template/report-new-preview/report-new-preview.component';
import {
  BeforeunloadGuard,
  ReportTemplateDesignComponent,
} from './report-template/report-template-design/report-template-design.component';
import {
  BeforeunloadGuardNew,
  ReportTemplateNewDesignComponent
} from './report-template/report-template-new-design/report-template-new-design.component';
import { ReportTemplateComponent } from './report-template/report-template.component';
import { SideNavBarComponent } from './side-nav-bar/side-nav-bar.component';
import { SvgIconComponent } from './svg-icon/svg-icon.component';
import { PropertyNewPreviewComponent } from './report-template/property-new-preview/property-new-preview.component';
import { DropDownListInputComponent } from './_shared/component/drop-down-list-input/drop-down-list-input.component';
import { LangUpdateService } from './_services/lang-update.service';
import { CustomDatePipe } from './custom-date.pipe';
import { EquipmentTypesPipe } from './equipment-types.pipe';
import { TenantUserService } from './_services/tenant-user.service';
import { TextsInfoInputComponent } from './_shared/component/texts-info-input/texts-info-input.component';
import { ConfigurationService } from '@osc-utils/services/configuration.service';
import { AuthService, getAuthProviders, PlatformService, provideEnv } from '@evc/platform';
import { OscUtilsModule } from '@osc-utils/osc-utils.module';
import { PlatformComponent } from '@evc/platform';
import { environment } from 'src/environments/environment';
import { ForbiddenInterceptor } from './auth/forbidden.interceptor';

export const initConfig = (
  configService: ConfigurationService,
) => {
  const configEndPoints = './endpoints-config/endpoints-config.json';
  return () => configService.load(configEndPoints);
};

const HttpLoaderFactory = (http: HttpClient): CustomTranslateHttpLoader =>
  CustomTranslateHttpLoader.CustomHttpLoaderFactory(http,
    [{ prefix: './assets/i18n/app/', suffix: '.json?tag=' + new Date().toLocaleString() },
    { prefix: 'assets/i18n/osc-reference-app/', suffix: '.json' }
    ]);


@NgModule({
  declarations: [
    AppComponent,
    SideNavBarComponent,
    SvgIconComponent,
    CommonSpinnerComponent,
    ReportTemplateComponent,
    ReportTemplateDesignComponent,
    ReportTemplateNewDesignComponent,
    ReportPreviewComponent,
    ReportNewPreviewComponent,
    PropertyPreviewComponent,
    PropertyNewPreviewComponent,
    PropertyEditorComponent,
    ReportPageComponent,
    ReportTextComponent,
    ReportLineComponent,
    ReportImageComponent,
    ReportRectangleComponent,
    ReportSummaryComponent,
    ReportSubTotalComponent,
    ReportPictureComponent,
    ReportDataPlateComponent,
    ReportDropDownListComponent,
    ReportTemplateEditPropertyDialogComponent,
    ReportTemplateEditReportNameFormatDialogComponent,
    ReportTemplateImageUploadDialogComponent,
    ReportTemplateMoveConfirmDialogComponent,
    CommonConfirmDialogComponent,
    AddNewTemplateDialogComponent,
    ReportMeasurementPointComponent,
    ReportTableComponent,
    VisualEventsComponent,
    CommonErrorMessageDialogComponent,
    EditableTextsValidationDialogComponent,
    CommonDeleteDialogComponent,
    ReportSignatureComponent,
    ReportSectionComponent,
    ReportIsoComponent,
    ImportNewTemplateDialogComponent,
    ListDialogComponent,
    CreateCustomElementComponent,
    ReportCustomElementComponent,
    CustomDatePipe,
    DropDownListInputComponent,
    EquipmentTypesPipe,
    TextsInfoInputComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    CookieModule.forRoot(),
    MaterialModule,
    MatExpansionModule,
    OscUtilsModule.forRoot(),
    OscReferenceAppCommonModule,
    ColorPickerModule,
    MatMenuModule,
    TimeagoModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      extend: true,
      isolate: false
    }),
    NgxCaptureModule,
    PlatformComponent
  ],
  providers: [
    provideEnv(environment),
    AuthService,
    DatePipe,
    UtilsService,
    ReportTemplateService,
    SubjectModelService,
    LangUpdateService,
    BeforeunloadGuard,
    TranslateService,
    BeforeunloadGuardNew,
    ReportComponentService,
    StorageService,
    TenantUserService,
    { provide: 'OSC_ENVIRONMENT', useFactory: getEnvironment },
    provideAppInitializer(() => initConfig(inject(ConfigurationService))()),
    provideAppInitializer(() => {
      // We make sure the PlatformService starts its initialization before any components, services, guards, or interceptors are created.
      // This way, we are certain that observables linked to platforms will eventually emit their values.
      // IMPORTANT: We cannot wait for initialization here because it would dead lock.
      inject(PlatformService).init();
    }),
    { provide: HTTP_INTERCEPTORS, useClass: ForbiddenInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
    ...getAuthProviders(environment)
  ]
})
export class AppModule { }
